import moment from 'moment'
import type { Constant } from '@pbt/pbt-ui-components'
import { Utils } from '@pbt/pbt-ui-components'

import { PrescriptionStates } from '~/constants/prescription'
import {
  DeclineReasonToDisplayStatus,
  HiddenDeclineReasons,
  RxDisplayStatus,
} from '~/constants/rxStatusChip'

export const RX_DISPLAY_DECLINED_STATUSES: ReadonlyArray<RxDisplayStatus> = (
  Object.values(RxDisplayStatus) as RxDisplayStatus[]
).filter((value) => value.startsWith('DECLINED_'))

export const isRxDisplayStatusIn = (
  status: RxDisplayStatus | null,
  candidates: ReadonlySet<RxDisplayStatus>,
): boolean => status != null && candidates.has(status)

export interface RxStatusInput {
  expirationDate?: string | null
  rejectionReason?: string | null
  stateId?: string | null
}

const getDeclinedDisplayStatus = (
  rejectionReason?: string | null,
): RxDisplayStatus | null => {
  if (!rejectionReason) {
    return null
  }

  const isHidden = HiddenDeclineReasons.some((hiddenReason) =>
    rejectionReason.includes(hiddenReason),
  )
  if (isHidden) {
    return null
  }

  if (rejectionReason.toLowerCase().includes('wrong product')) {
    return RxDisplayStatus.DECLINED_WRONG_PRODUCT
  }

  if (rejectionReason.toLowerCase().includes('wrong pet')) {
    return RxDisplayStatus.DECLINED_WRONG_PET
  }

  const matchedEntry = Object.entries(DeclineReasonToDisplayStatus).find(
    ([reasonType]) => rejectionReason.includes(reasonType),
  )

  return matchedEntry ? matchedEntry[1] : RxDisplayStatus.DECLINED_OTHER
}

export const getRxDisplayStatus = (
  input: RxStatusInput,
  prescriptionStates?: Constant[],
): RxDisplayStatus | null => {
  const { stateId, rejectionReason, expirationDate } = input

  if (expirationDate && moment(expirationDate).isBefore(moment())) {
    return RxDisplayStatus.EXPIRED
  }

  const stateName = stateId
    ? Utils.getConstantName(stateId, prescriptionStates ?? [])
    : null

  if (!stateName) {
    return null
  }

  const stateToDisplayStatus: Record<
    string,
    (rejectionReason?: string | null) => RxDisplayStatus | null
  > = {
    [PrescriptionStates.APPROVED]: () => RxDisplayStatus.VET_APPROVED,
    [PrescriptionStates.PRESCRIBED]: () => RxDisplayStatus.VET_APPROVED,
    [PrescriptionStates.PENDING_VET_APPROVAL]: () =>
      RxDisplayStatus.PENDING_VET_APPROVAL,
    [PrescriptionStates.CANCELED]: () => RxDisplayStatus.CANCELED,
    [PrescriptionStates.DECLINED]: (reason) => getDeclinedDisplayStatus(reason),
  }

  const statusHandler = stateToDisplayStatus[stateName]
  return statusHandler ? statusHandler(rejectionReason) : null
}
